import { useInfiniteQuery, useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { datasets, type CandleRequest, type EventRequest } from './datasets';
import { strategies } from './strategies';
import { backtests } from './backtests';
import { usersApi } from './users';
import { tradingApi, type CreateGatewayRequest, type CreateTradingDeploymentRequest, type SaveTradingDeploymentInputsRequest, type TradingOverviewDto } from './trading';
import type {
  BacktestCreateRequest,
  BacktestRunDto,
  CandleChunkDto,
  ChartEventPageDto,
  DatasetDetailDto,
  DatasetSummaryDto,
  NodeDescriptor,
  StrategyDocumentDto,
  StrategyResourceDto,
  StrategyCatalogDto,
  StrategySummaryDto,
  CreateUserRequest,
  UpdateUserRequest,
  UserSummaryDto,
  TimeframeCapabilitiesDto,
} from './types';

// ── Query Keys ──

export const queryKeys = {
  datasets: ['datasets'] as const,
  dataset: (id: string) => ['datasets', id] as const,
  datasetCandles: (id: string, req: CandleRequest) =>
    ['datasets', id, 'candles', req] as const,
  datasetEvents: (id: string, req: EventRequest) =>
    ['datasets', id, 'events', req] as const,
  timeframeCapabilities: (datasetId = '') => ['timeframe-capabilities', datasetId] as const,
  strategies: ['strategies'] as const,
  strategy: (id: string) => ['strategies', id] as const,
  backtests: ['backtests'] as const,
  backtest: (id: string) => ['backtests', id] as const,
  backtestReplaySummary: (id: string) => ['backtests', id, 'replay', 'summary'] as const,
  backtestReplayEvents: (id: string, kind: string, search: string, datasetId: string) => ['backtests', id, 'replay', 'events', kind, search, datasetId] as const,
  backtestReplayTrades: (id: string, direction: string, outcome: string, datasetId: string) => ['backtests', id, 'replay', 'trades', direction, outcome, datasetId] as const,
  backtestReplayEvent: (id: string, eventId: string) => ['backtests', id, 'replay', 'event', eventId] as const,
  backtestReplayTrade: (id: string, tradeId: string) => ['backtests', id, 'replay', 'trade', tradeId] as const,
  backtestSnapshot: (id: string) => ['backtests', id, 'snapshot'] as const,
  nodeTypes: ['node-types'] as const,
  users: ['users'] as const,
  tradingGateways: ['trading', 'gateways'] as const,
  tradingOverview: ['trading', 'overview'] as const,
  tradingQuotes: ['trading', 'quotes'] as const,
  tradingGateway: (id: string) => ['trading', 'gateways', id] as const,
  tradingGatewayQuotes: (id: string) => ['trading', 'gateways', id, 'quotes'] as const,
  tradingDeployments: ['trading', 'deployments'] as const,
  tradingDeployment: (id: string) => ['trading', 'deployments', id] as const,
  tradingResource: (id: string, resource: string) => ['trading', 'deployments', id, resource] as const,
};

type ReplayPageParam = { before?: string; after?: string };

// ── Datasets ──

export function useDatasets(options?: Omit<UseQueryOptions<DatasetSummaryDto[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.datasets,
    queryFn: () => datasets.list(),
    ...options,
  });
}

export function useDataset(
  id: string,
  options?: Omit<UseQueryOptions<DatasetDetailDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.dataset(id),
    queryFn: () => datasets.get(id),
    enabled: !!id,
    ...options,
  });
}

export function useDatasetCandles(
  id: string,
  request: CandleRequest,
  options?: Omit<UseQueryOptions<CandleChunkDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.datasetCandles(id, request),
    queryFn: () => datasets.fetchCandles(id, request),
    enabled: !!id,
    ...options,
  });
}

export function useDatasetEvents(
  id: string,
  request: EventRequest,
  options?: Omit<UseQueryOptions<ChartEventPageDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.datasetEvents(id, request),
    queryFn: () => datasets.fetchEvents(id, request),
    enabled: !!id,
    ...options,
  });
}

export function useCreateDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => datasets.create(formData),
    onSuccess: (result) => {
      if (result.success && result.dataset?.id) {
        qc.setQueryData(queryKeys.dataset(result.dataset.id), result.dataset);
      }
      return qc.invalidateQueries({ queryKey: queryKeys.datasets, exact: true });
    },
  });
}

export function useTimeframeCapabilities(
  datasetId = '',
  options?: Omit<UseQueryOptions<TimeframeCapabilitiesDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.timeframeCapabilities(datasetId),
    queryFn: ({ signal }) => datasets.timeframeCapabilities(datasetId || undefined, signal),
    ...options,
  });
}

export function useUpdateDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) => datasets.update(id, formData),
    onSuccess: (result, { id }) => {
      if (result.success && result.dataset) {
        qc.setQueryData(queryKeys.dataset(id), result.dataset);
      }
      return qc.invalidateQueries({ queryKey: queryKeys.datasets, exact: true });
    },
  });
}

export function useDeleteDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => datasets.delete(id),
    onSuccess: async (_, id) => {
      qc.removeQueries({ queryKey: queryKeys.dataset(id) });
      await qc.invalidateQueries({ queryKey: queryKeys.datasets, exact: true });
    },
  });
}

// ── Strategies ──

export function useStrategies(options?: Omit<UseQueryOptions<StrategySummaryDto[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.strategies,
    queryFn: () => strategies.list(),
    ...options,
  });
}

export function useStrategy(
  id: string,
  options?: Omit<UseQueryOptions<StrategyDocumentDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.strategy(id),
    queryFn: () => strategies.get(id),
    enabled: !!id,
    ...options,
  });
}

export function useNodeTypes(options?: Omit<UseQueryOptions<NodeDescriptor[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.nodeTypes,
    queryFn: () => strategies.nodeTypes(),
    ...options,
  });
}

export function useCreateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (doc: StrategyDocumentDto) => strategies.create(doc),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

export function useUpdateStrategyName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => strategies.updateName(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

export function useCloneStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => strategies.clone(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

export function useDeleteStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => strategies.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

export function useValidateStrategy() {
  return useMutation({
    mutationFn: (doc: StrategyDocumentDto) => strategies.validate(doc),
  });
}

// ── Backtests ──

export function useBacktests(options?: Omit<UseQueryOptions<BacktestRunDto[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.backtests,
    queryFn: () => backtests.list(),
    ...options,
  });
}

export function useBacktest(
  id: string,
  options?: Omit<UseQueryOptions<BacktestRunDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.backtest(id),
    queryFn: () => backtests.get(id),
    enabled: !!id,
    ...options,
  });
}

export function useBacktestSnapshot(
  id: string,
  options?: Omit<UseQueryOptions<StrategyDocumentDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.backtestSnapshot(id),
    queryFn: () => backtests.getStrategySnapshot(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateBacktest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: BacktestCreateRequest) => backtests.create(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.backtests }),
  });
}

export function useStrategyResource(
  id: string,
  options?: Omit<UseQueryOptions<StrategyResourceDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...queryKeys.strategy(id), 'resource'],
    queryFn: () => strategies.getResource(id),
    enabled: !!id,
    ...options,
  });
}

export function useStrategyCatalog(options?: Omit<UseQueryOptions<StrategyCatalogDto, Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({ queryKey: queryKeys.nodeTypes, queryFn: () => strategies.catalog(), ...options });
}

export function useBacktestReplaySummary(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.backtestReplaySummary(id),
    queryFn: ({ signal }) => backtests.getReplaySummary(id, signal),
    enabled: !!id && enabled,
  });
}

export function useBacktestReplayEvents(id: string, kind: string, search: string, datasetId = 'All', enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.backtestReplayEvents(id, kind, search, datasetId),
    queryFn: ({ pageParam, signal }) => backtests.getReplayEvents(id, {
      ...pageParam,
      limit: 200,
      kind: kind === 'All' ? undefined : kind,
      search: search.trim() || undefined,
      datasetId: datasetId === 'All' ? undefined : datasetId,
    }, signal),
    initialPageParam: {} as ReplayPageParam,
    getNextPageParam: (lastPage): ReplayPageParam | undefined => lastPage.hasMoreAfter && lastPage.cursorAfter ? { after: lastPage.cursorAfter } : undefined,
    getPreviousPageParam: (firstPage): ReplayPageParam | undefined => firstPage.hasMoreBefore && firstPage.cursorBefore ? { before: firstPage.cursorBefore } : undefined,
    maxPages: 8,
    enabled: !!id && enabled,
  });
}

export function useBacktestReplayTrades(id: string, direction: string, outcome: string, datasetId = 'All', enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.backtestReplayTrades(id, direction, outcome, datasetId),
    queryFn: ({ pageParam, signal }) => backtests.getReplayTrades(id, {
      ...pageParam,
      limit: 200,
      direction: direction === 'All' ? undefined : direction,
      outcome: outcome === 'All' ? undefined : outcome,
      datasetId: datasetId === 'All' ? undefined : datasetId,
    }, signal),
    initialPageParam: {} as ReplayPageParam,
    getNextPageParam: (lastPage): ReplayPageParam | undefined => lastPage.hasMoreAfter && lastPage.cursorAfter ? { after: lastPage.cursorAfter } : undefined,
    getPreviousPageParam: (firstPage): ReplayPageParam | undefined => firstPage.hasMoreBefore && firstPage.cursorBefore ? { before: firstPage.cursorBefore } : undefined,
    maxPages: 8,
    enabled: !!id && enabled,
  });
}

export function useBacktestReplayEvent(id: string, eventId: string | null) {
  return useQuery({
    queryKey: queryKeys.backtestReplayEvent(id, eventId ?? ''),
    queryFn: ({ signal }) => backtests.getReplayEvent(id, eventId!, signal),
    enabled: !!id && !!eventId,
  });
}

export function useBacktestReplayTrade(id: string, tradeId: string | null) {
  return useQuery({
    queryKey: queryKeys.backtestReplayTrade(id, tradeId ?? ''),
    queryFn: ({ signal }) => backtests.getReplayTrade(id, tradeId!, signal),
    enabled: !!id && !!tradeId,
  });
}

export function useCloneBacktestSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => backtests.cloneStrategySnapshot(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

// Users

export function useUsers() {
  return useQuery({ queryKey: queryKeys.users, queryFn: usersApi.list });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateUserRequest) => usersApi.create(request),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateUserRequest }) =>
      usersApi.update(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ user, temporaryPassword }: { user: UserSummaryDto; temporaryPassword: string }) =>
      usersApi.resetPassword(user.id, temporaryPassword),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

// Trading administration

export function useTradingGateways() {
  return useQuery({
    queryKey: queryKeys.tradingGateways,
    queryFn: ({ signal }) => tradingApi.gateways(signal),
    refetchInterval: (query) => query.state.data?.some((item) => item.connected) ? 2_000 : 10_000,
  });
}

export function useTradingOverview(
  includeArchived = false,
  options?: Omit<UseQueryOptions<TradingOverviewDto, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...queryKeys.tradingOverview, { includeArchived }],
    queryFn: ({ signal }) => tradingApi.overview(includeArchived, signal),
    refetchInterval: (query) => query.state.data?.deployments.some((item) =>
      ['Starting', 'Reconciling', 'WarmingUp', 'Active', 'Offline', 'RiskPaused', 'StopPending'].includes(item.status)) ? 2_000 : 10_000,
    ...options,
  });
}

export function useTradingGateway(id: string) {
  return useQuery({
    queryKey: queryKeys.tradingGateway(id),
    queryFn: ({ signal }) => tradingApi.gateway(id, signal),
    enabled: !!id,
    refetchInterval: (query) => query.state.data?.connected ? 2_000 : 10_000,
  });
}

export function useTradingMarketQuotes(streaming: boolean) {
  return useQuery({
    queryKey: queryKeys.tradingQuotes,
    queryFn: ({ signal }) => tradingApi.quotes(signal),
    enabled: streaming,
    refetchInterval: streaming ? 1_000 : false,
  });
}

export function useTradingQuotes(id: string, streaming: boolean) {
  return useQuery({
    queryKey: queryKeys.tradingGatewayQuotes(id),
    queryFn: ({ signal }) => tradingApi.gatewayQuotes(id, signal),
    enabled: !!id && streaming,
    refetchInterval: streaming ? 1_000 : false,
  });
}

export function useTradingDeployments(includeArchived = false) {
  return useQuery({
    queryKey: [...queryKeys.tradingDeployments, { includeArchived }],
    queryFn: ({ signal }) => tradingApi.deployments(includeArchived, signal),
    refetchInterval: (query) => query.state.data?.some((item) =>
      ['Starting', 'Reconciling', 'WarmingUp', 'Active', 'Offline', 'StopPending'].includes(item.status)) ? 2_000 : 10_000,
  });
}

export function useCreateTradingGateway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateGatewayRequest) => tradingApi.createGateway(request),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.tradingGateways }),
      qc.invalidateQueries({ queryKey: queryKeys.tradingOverview }),
    ]),
  });
}

export function useRotateTradingGatewayKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tradingApi.rotateGatewayKey(id),
    onSuccess: (_, id) => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.tradingGateways }),
      qc.invalidateQueries({ queryKey: queryKeys.tradingGateway(id) }),
    ]),
  });
}

export function useRevokeTradingGateway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tradingApi.revokeGateway(id),
    onSuccess: (_, id) => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.tradingGateways }),
      qc.invalidateQueries({ queryKey: queryKeys.tradingGateway(id) }),
    ]),
  });
}

export function useCreateTradingDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateTradingDeploymentRequest) => tradingApi.createDeployment(request),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.tradingDeployments }),
      qc.invalidateQueries({ queryKey: queryKeys.tradingOverview }),
    ]),
  });
}

export function useTradingDeploymentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, request }: { id: string; action: 'start' | 'restart' | 'stop' | 'flatten' | 'force-stop' | 'retire' | 'force-retire'; request?: { expectedAccountId?: string; acknowledgement?: string; reason?: string } }) => {
      if (action === 'force-stop') return tradingApi.forceStop(id, { acknowledgement: request?.acknowledgement ?? '', reason: request?.reason ?? '' });
      if (action === 'retire' || action === 'force-retire') return tradingApi.retire(id, { forceRelease: action === 'force-retire', acknowledgement: request?.acknowledgement, reason: request?.reason });
      return tradingApi.action(id, action, request);
    },
    onSuccess: (_, { id }) => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.tradingDeployments }),
      qc.invalidateQueries({ queryKey: queryKeys.tradingDeployment(id) }),
      qc.invalidateQueries({ queryKey: queryKeys.tradingOverview }),
    ]),
  });
}

export function useTradingDeployment(id: string) {
  return useQuery({
    queryKey: queryKeys.tradingDeployment(id),
    queryFn: ({ signal }) => tradingApi.deployment(id, signal),
    enabled: !!id,
    refetchInterval: (query) => ['Starting', 'Reconciling', 'WarmingUp', 'Active', 'Offline', 'RiskPaused', 'StopPending']
      .includes(query.state.data?.status ?? '') ? 2_000 : 10_000,
  });
}

export function useTradingPreflight(id: string) {
  return useQuery({
    queryKey: queryKeys.tradingResource(id, 'preflight'),
    queryFn: ({ signal }) => tradingApi.preflight(id, signal),
    enabled: !!id,
  });
}

export function useTradingDeploymentInputs(id: string) {
  return useQuery({
    queryKey: [...queryKeys.tradingDeployment(id), 'inputs'],
    queryFn: ({ signal }) => tradingApi.getDeploymentInputs(id, signal),
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

export function useSaveTradingDeploymentInputs(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveTradingDeploymentInputsRequest) => tradingApi.saveDeploymentInputs(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.tradingDeployment(id) }),
  });
}
